import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useSWRConfig } from "swr";
import { useAuth } from "@/auth/auth";
import { Button } from "@/components/ui/button";
import {
	AlertDialog,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
} from "@/components/ui/alert-dialog";

type Props = {
	kind: "xcode" | "ios";
	organizationId: string;
	snapshotKey: string;
	onDeleted?: () => void;
};

export function SnapshotDeleteButton(props: Props) {
	return <SnapshotDeleteAction key={`${props.organizationId}:${props.kind}:${props.snapshotKey}`} {...props} />;
}

function SnapshotDeleteAction({ kind, organizationId, snapshotKey, onDeleted }: Props) {
	const auth = useAuth();
	const { mutate } = useSWRConfig();
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();

	async function remove() {
		setBusy(true);
		setError(undefined);
		try {
			const path = kind === "xcode" ? "/v1/organizations/{organizationId}/xcode_snapshots/{key}" : "/v1/organizations/{organizationId}/ios_snapshots/{key}";
			const result = await auth.client.DELETE(path, { params: { path: { organizationId, key: snapshotKey } } });
			if (result.error && result.response.status !== 404) throw new Error(result.error.message);
			// Revalidate the list, detail, and picker so a deleted key cannot stay selected elsewhere.
			void mutate((key) =>
				typeof key === "string"
					? key.startsWith(`/v1/organizations/${organizationId}/xcode_snapshots`)
					: Array.isArray(key) && key[1] === organizationId && (key[0] === "ios-snapshots" || key[0] === "ios-snapshot" || key[0] === "xcode-snapshot"),
			);
			setOpen(false);
			onDeleted?.();
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : "Failed to delete snapshot. Try again.");
		} finally {
			setBusy(false);
		}
	}

	return (
		<AlertDialog
			open={open}
			onOpenChange={(value) => {
				if (!busy) {
					setOpen(value);
					setError(undefined);
				}
			}}
		>
			<AlertDialogTrigger asChild>
				<Button variant="ghost" size="sm" aria-label={`Delete snapshot ${snapshotKey}`}>
					<Trash2 className="h-4 w-4" />
					Delete
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Delete snapshot?</AlertDialogTitle>
					<AlertDialogDescription className="break-words">
						Permanently delete <strong className="break-all">{snapshotKey}</strong> and its saved {kind === "xcode" ? "workspace" : "apps and data"}. Running
						instances can save this key again. Stop instances that save this key first if you want it to remain empty.
					</AlertDialogDescription>
				</AlertDialogHeader>
				{error && (
					<p role="alert" className="text-sm text-danger">
						{error}
					</p>
				)}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
					<Button variant="destructive" disabled={busy} onClick={() => void remove()}>
						{busy ? "Deleting…" : "Delete snapshot"}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
